import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateListValue,
  updateFieldValue,
  addItemToArray,
  updateObjectArrayValue,
  setCurrentTab,
  clearInvestmentData,
  removeItemFromArray,
  handleNearbyLocations,
} from '../../../redux/project/investmentSlice';
import { AppDispatch, RootState } from '../../../redux/store';
import ProjectForm from './InvestmentForm';
import Swal from 'sweetalert2';
import { ModuleType } from '../../../api/modules.types';
import CreateApi from '../../../api/genericApis/Create.api';

const AddInvestment = () => {
  const dispatch: AppDispatch = useDispatch();
  const investmentData = useSelector((state: RootState) => state.investment);
  const currentTab = investmentData.currentTab; // Get currentTab from Redux
  const totalTabs = 8; // Total number of steps (tabs)

  // Handle input changes for fields
  const handleInputChange = ({
    e,
    section,
    field,
    lang,
    isSameValue,
    index,
  }: {
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
    section: string;
    field: string;
    lang: 'ar' | 'en';
    isSameValue: boolean;
    index?: number;
  }) => {
    const { value } = e.target;

    if (index !== undefined) {
      dispatch(
        updateListValue({
          language: lang,
          section,
          field,
          index,
          value,
          isSameValue,
        }),
      );
    } else {
      dispatch(
        updateFieldValue({
          language: lang,
          section,
          field,
          value,
          isSameValue,
        }),
      );
    }
  };

  const handleObjectArrayInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    section: string,
    field: string,
    index: number,
    key: string,
    lang: 'ar' | 'en',
    isSameValue: boolean,
  ) => {
    const { value } = e.target;
    dispatch(
      updateObjectArrayValue({
        language: lang,
        section,
        field,
        index,
        key,
        value,
        isSameValue,
      }),
    );
  };

  const handleAddItem = (section: string, field: string, value?: any) => {
    dispatch(addItemToArray({ section, field, value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Confirm with SweetAlert before submitting
    Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to submit the investment opportunity?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, submit!',
      cancelButtonText: 'No, cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        CreateApi({ moduleType: ModuleType.investment, data: investmentData })
          .then((response) => {
            // On success
            Swal.fire({
              title: 'Success!',
              text: 'Investment opportunity has been successfully added.',
              icon: 'success',
              confirmButtonText: 'Ok',
            });

            // Clear fields after success
            dispatch(clearInvestmentData());
          })
          .catch((error) => {
            Swal.fire({
              title: 'Error!',
              text: 'There was an issue submitting the Investment opportunity. Please try again.',
              icon: 'error',
              confirmButtonText: 'Ok',
            });
          });
      }
    });
  };

  // Handle tab change in Redux store
  const handleTabChange = (key: number) => {
    dispatch(setCurrentTab(key)); // Update the current tab in Redux
  };

  // Handle Next/Previous buttons
  const handleNext = () => {
    if (currentTab < totalTabs - 1) {
      dispatch(setCurrentTab(currentTab + 1)); // Move to next tab
    }
  };

  const handlePrevious = () => {
    if (currentTab > 0) {
      dispatch(setCurrentTab(currentTab - 1)); // Move to previous tab
    }
  };

  return (
    <ProjectForm
      currentTab={currentTab}
      totalTabs={totalTabs}
      investmentData={investmentData}
      handleInputChange={handleInputChange}
      handleAddItem={handleAddItem}
      handleObjectArrayInputChange={handleObjectArrayInputChange}
      handleTabChange={handleTabChange}
      handleNext={handleNext}
      handlePrevious={handlePrevious}
      handleSubmit={handleSubmit}
      removeItemFromArray={removeItemFromArray}
      handleNearbyLocations={handleNearbyLocations}
      title="Add Investment Opportunity"
      flow="add"
    />
  );
};

export default AddInvestment;
